import React, { useState, useEffect, useCallback } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import { Icon, Popover, Loader } from '@clientbase/clientbase-library'
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Alert,
  Fade
} from '@mui/material'
import useTagOperations from 'hooks/useTagOperations'
import useTags from 'hooks/useTags'

import ChipTag from 'components/Tag'
import TagsAddInput from 'components/TagsAddInput'
import { Tag } from 'components/TagsAddInput/TagsAddInput'

import { theme } from 'styles/theme'

export type SaveMode = 'immediate' | 'deferred'

export interface SaveContext {
  taggableType:
    | 'Customer'
    | 'Billing'
    | 'Recurrence'
    | 'ProductLink'
    | 'Product'
    | 'AccountsPayableRecurrence'
    | 'AccountsPayable'
    | 'Installment'
    | 'RegisterLink'
  taggableUuid: string
}

interface TagsPopoverProps<T extends FieldValues> {
  control: Control<T, object>
  name: Path<T>
  disabled?: boolean
  maxTags?: number
  // New dual-mode props
  saveMode?: SaveMode
  saveContext?: SaveContext
  onSave?: (tags: Tag[]) => void | Promise<void>
  onOptimisticUpdate?: (tags: Tag[], isOptimistic: boolean) => void
  hideSearchInput?: boolean
}

const TagsPopover = <T extends FieldValues>({
  control,
  name,
  disabled = false,
  maxTags = 10,
  saveMode = 'deferred',
  saveContext,
  onSave,
  onOptimisticUpdate,
  hideSearchInput = false
}: TagsPopoverProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [, setLocalTags] = useState<Tag[]>([])
  const [, setOptimisticUpdates] = useState<Set<string>>(new Set())
  const [rollbackState, setRollbackState] = useState<Tag[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { tags: availableTags, createTag } = useTags()
  const { linkTagImmediate, unlinkTagImmediate, linkState, unlinkState } =
    useTagOperations()

  // Initialize local tags state from form value
  useEffect(() => {
    const formValue = control._getWatch(name) as Tag[]
    if (formValue && Array.isArray(formValue)) {
      setLocalTags(formValue)
    }
  }, [control, name])

  // Clear error after a timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [error])

  // Função para marcar o formulário como modificado
  const markFormAsDirty = useCallback(() => {
    // Não marcar o formulário como dirty para tags quando em modo immediate
    // pois as tags são salvas imediatamente no servidor
    if (saveMode === 'immediate') {
      return
    }

    setTimeout(() => {
      if (control._formState) {
        control._formState.isDirty = true
        // Marcar especificamente o campo como dirty
        if (control._formState.dirtyFields) {
          ;(control._formState.dirtyFields as any)[name] = true
        }
      }
    }, 0)
  }, [control, name, saveMode])

  // Handle immediate mode tag operations
  const handleImmediateTagOperation = useCallback(
    async (
      operation: 'add' | 'remove',
      tag: Tag,
      currentTags: Tag[]
    ): Promise<Tag[]> => {
      if (!saveContext) {
        throw new Error('Save context required for immediate mode')
      }

      // Create optimistic update
      const operationId = `${operation}-${tag.id || tag.name}-${Date.now()}`
      setOptimisticUpdates((prev) => new Set([...prev, operationId]))

      // Store rollback state
      setRollbackState(currentTags)

      // Optimistic UI update
      const optimisticTags =
        operation === 'add'
          ? [...currentTags, tag]
          : currentTags.filter((t) => t.id !== tag.id && t.name !== tag.name)

      if (onOptimisticUpdate) {
        onOptimisticUpdate(optimisticTags, true)
      }

      try {
        if (operation === 'add') {
          await linkTagImmediate(tag.id, {
            taggable_type: saveContext.taggableType,
            taggable_uuid: saveContext.taggableUuid
          })
          return [...currentTags, tag]
        } else {
          await unlinkTagImmediate(tag.id, {
            taggable_type: saveContext.taggableType,
            taggable_uuid: saveContext.taggableUuid
          })
          return currentTags.filter(
            (t) => t.id !== tag.id && t.name !== tag.name
          )
        }
      } catch (error: any) {
        // Rollback on error
        setError(
          `Erro ao ${operation === 'add' ? 'adicionar' : 'remover'} tag: ${
            error.message
          }`
        )
        throw error
      } finally {
        setOptimisticUpdates((prev) => {
          const newSet = new Set(prev)
          newSet.delete(operationId)
          return newSet
        })
      }
    },
    [saveContext, linkTagImmediate, unlinkTagImmediate]
  )

  // Handle deferred mode operations
  const handleDeferredTagOperation = useCallback(
    (operation: 'add' | 'remove', tag: Tag, currentTags: Tag[]): Tag[] => {
      if (operation === 'add') {
        return [...currentTags, tag]
      } else {
        return currentTags.filter((t) => t.id !== tag.id && t.name !== tag.name)
      }
    },
    []
  )

  const isOpen = Boolean(anchorEl)
  const popoverId = isOpen ? 'tags-popover' : undefined

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRemoveTag = useCallback(
    async (
      tagToRemove: Tag,
      currentValue: Tag[],
      onChange: (tags: Tag[]) => void
    ) => {
      try {
        let newTags: Tag[]

        if (saveMode === 'immediate') {
          newTags = await handleImmediateTagOperation(
            'remove',
            tagToRemove,
            currentValue
          )
        } else {
          newTags = handleDeferredTagOperation(
            'remove',
            tagToRemove,
            currentValue
          )
        }

        onChange(newTags)
        setLocalTags(newTags)

        // Call onSave if provided
        if (onSave) {
          await onSave(newTags)
        }

        // Marcar o formulário como modificado
        markFormAsDirty()
      } catch (error) {
        // Error handled by operation functions
        // Rollback will happen automatically
        if (rollbackState) {
          onChange(rollbackState)
          setLocalTags(rollbackState)
        }
      }
    },
    [
      saveMode,
      handleImmediateTagOperation,
      handleDeferredTagOperation,
      onSave,
      markFormAsDirty,
      rollbackState
    ]
  )

  const getTagCount = (tags: Tag[]) => {
    return tags?.length || 0
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value = [], onChange }, fieldState }) => (
        <>
          <Tooltip title="Gerenciar tags" arrow>
            <IconButton
              onClick={handleClick}
              disabled={disabled}
              sx={{
                color:
                  getTagCount(value) > 0
                    ? theme.palette.primary.main
                    : theme.palette.neutral[200],
                position: 'relative',
                '&:hover': {
                  backgroundColor: theme.palette.primary[50]
                }
              }}
              aria-describedby={popoverId}
            >
              <Icon icon="bookmark" />
              {getTagCount(value) > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.neutral[200],
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}
                >
                  {getTagCount(value)}
                </Box>
              )}
            </IconButton>
          </Tooltip>

          <Popover
            id={popoverId}
            open={isOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            PaperProps={{
              sx: {
                width: '380px',
                maxHeight: 400,
                overflow: 'visible',
                position: 'relative'
              }
            }}
          >
            {/* Loading overlay for immediate mode */}
            {saveMode === 'immediate' &&
              (linkState.loading || unlinkState.loading) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                  }}
                >
                  <Loader />
                </Box>
              )}
            <Box p={2}>
              <Stack gap={2}>
                {/* Error display */}
                {error && (
                  <Fade in={!!error}>
                    <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[200]}
                    sx={{ marginRight: '6px' }}
                  >
                    Tags selecionadas ({getTagCount(value)})
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    arrow
                    placement="right"
                    title="Tags são etiquetas que ajudam a organizar e categorizar suas cobranças para facilitar a busca e filtragem"
                  >
                    <Box
                      ml="-12px"
                      display={'flex'}
                      alignItems="center"
                      justifyContent={'top'}
                    >
                      <Icon
                        icon="info"
                        sx={{
                          fontSize: '1.2rem',
                          color: theme.palette.neutral[600],
                          marginTop: '1px'
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Stack>

                <Divider />

                {/* Tags selecionadas */}
                {value.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {value.map((tag: Tag) => (
                      <ChipTag
                        key={tag.id}
                        tag={{
                          ...tag,
                          name: tag.name || 'Tag sem nome'
                        }}
                        showDeleteButton={true}
                        deleteContext={
                          saveMode === 'immediate' ? 'details' : 'form'
                        }
                        onDeleteImmediate={
                          saveMode === 'immediate'
                            ? () => handleRemoveTag(tag, value, onChange)
                            : undefined
                        }
                        onDelete={
                          saveMode === 'deferred'
                            ? () => handleRemoveTag(tag, value, onChange)
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                )}

                {/* Componente para adicionar tags */}
                {!hideSearchInput && (
                  <TagsAddInput
                    value={value}
                    onChange={async (newTags) => {
                      // Handle tag additions through dual-mode logic
                      const addedTags = newTags.filter(
                        (newTag: Tag) =>
                          !value.some(
                            (existingTag: Tag) =>
                              existingTag.id === newTag.id ||
                              existingTag.name === newTag.name
                          )
                      )

                      try {
                        const resultTags = newTags

                        // Process new tags through immediate mode if needed
                        if (saveMode === 'immediate' && addedTags.length > 0) {
                          for (const addedTag of addedTags) {
                            await handleImmediateTagOperation(
                              'add',
                              addedTag,
                              value
                            )
                          }
                        }

                        // Update local state
                        onChange(resultTags)
                        setLocalTags(resultTags)

                        // Call onSave if provided
                        if (onSave) {
                          await onSave(resultTags)
                        }

                        markFormAsDirty()
                      } catch (error) {
                        // Handle error and rollback
                        if (rollbackState) {
                          onChange(rollbackState)
                          setLocalTags(rollbackState)
                        }
                      }
                    }}
                    availableTags={availableTags}
                    onCreateTag={createTag}
                    onMarkFormAsDirty={markFormAsDirty}
                    maxTags={maxTags}
                    disabled={
                      disabled ||
                      (saveMode === 'immediate' &&
                        (linkState.loading || unlinkState.loading))
                    }
                  />
                )}

                {maxTags && value.length >= maxTags && (
                  <Typography
                    variant="xsLight"
                    color={theme.palette.warning.main}
                  >
                    Limite máximo de {maxTags} tags atingido
                  </Typography>
                )}

                {/* Form field errors */}
                {fieldState.error && (
                  <Typography
                    variant="xsLight"
                    color={theme.palette.error.main}
                  >
                    {fieldState.error.message}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Popover>
        </>
      )}
    />
  )
}

export default TagsPopover
